import {
    AfterViewInit,
    Component,
    EventEmitter,
    Output,
    ViewChild,
} from '@angular/core';
import { BarcodeFormat } from '@zxing/library';
import { ZXingScannerComponent, ZXingScannerModule } from '@zxing/ngx-scanner';
import { BarcodeProduct } from '../../../generic/classes/barcode.product';
import { Logger } from '../../../generic/logging/default-log.service';
import { ApplicationService } from '../../../generic/service/application.service';
import { BarcodeScannerService } from '../../../generic/service/barcode.scanner.service';
import { ProductService } from '../../../generic/service/product.service';
import { NgClass } from '@angular/common';

@Component({
    selector: 'barcode-scanner',
    templateUrl: 'barcode.scanner.component.html',
    styleUrls: ['../../../../assets/style/barcode.scanner.component.scss'],
    standalone: true,
    imports: [NgClass, ZXingScannerModule],
})
export class BarcodeScannerComponent implements AfterViewInit {
    @ViewChild('scanner')
    scanner: ZXingScannerComponent;

    // General properties
    popupOpen: boolean = false;

    // Barcde properties
    scannedResult: boolean = false;
    barcodeValue: string;
    barcodeProductResponse: BarcodeProduct;
    barcodeValid: boolean = false;
    barcodeProductNotFound: boolean = false;

    // Devices/Cameras
    defaultDevice: number = 0;
    availableDevices: MediaDeviceInfo[] = [];
    multipleDevices: boolean = false;

    // Allowed formats to scan using the scanner
    allowedFormats: BarcodeFormat[] = [
        BarcodeFormat.CODE_128,
        BarcodeFormat.DATA_MATRIX,
        BarcodeFormat.EAN_13,
        BarcodeFormat.QR_CODE,
    ];

    @Output() closed = new EventEmitter<boolean>();

    constructor(
        public applicationService: ApplicationService,
        private barcodeScannerService: BarcodeScannerService,
        private productService: ProductService,
        private logger: Logger,
    ) {
        this.popupOpen = true;
    }

    ngAfterViewInit(): void {
        this.scanner.camerasFound.subscribe((devices: MediaDeviceInfo[]) => {
            this.availableDevices = devices;

            if (devices.length > 1) {
                this.multipleDevices = true;
            }
        });
    }

    /**
     * Event listener that fires after a barcode was successfully scanned by the scanner.
     *
     * @param scannedResult - Result of the scanned barcode/qr code
     */
    scanSuccessHandler(scannedResult): void {
        this.barcodeValue = scannedResult;
        this.barcodeScannerService.getBarcodeProduct(scannedResult).subscribe(
            barcodeProduct => this.setBarcodeProduct(barcodeProduct),
            error => this.logger.error('Failed to get barcode product', error),
        );
    }

    setBarcodeProduct(barcodeProduct: BarcodeProduct): void {
        this.scannedResult = true;
        const outputStatus = barcodeProduct.outputStatus;

        if (outputStatus === 1) {
            this.barcodeValid = false;
            this.barcodeProductNotFound = true;
            this.logger.warn(
                `Barcode scan failed with the following message: ${barcodeProduct.outputMessage}`,
            );
            return;
        }

        this.barcodeValid = true;
        this.barcodeProductResponse = barcodeProduct;
        this.redirectToProductDetail();
    }

    /**
     * Close the popup and notify listeners
     */
    close(): void {
        // Make sure modal--hide is set, and close the popup application wide after 0.4s
        this.popupOpen = false;
        setTimeout(() => {
            this.closed.emit(false);
            this.applicationService.closeBarcodeScannerPopup();
        }, 400);
    }

    /**
     * For devices with multiple cameras, this will switch to each camera on every click.
     * Usually the device hold 2 cameras (front and back)
     */
    switchCamera(): void {
        if (this.multipleDevices) {
            this.defaultDevice = this.defaultDevice + 1;
            this.defaultDevice =
                this.defaultDevice % this.availableDevices.length;
            this.scanner.device = this.availableDevices[this.defaultDevice];
            return;
        }

        this.logger.info('Could not find more then one device');
    }

    /**
     * Redirect the user on request. Using the scanned barcode information to build the correct url
     */
    redirectToProductDetail(): void {
        window.location.href = this.productService.getProductDetailUrl(
            this.barcodeProductResponse,
            true,
        );
    }
}
